import db from '@/plugins/database'
import apicore, { logout as logoutFromApi } from '@/plugins/api'
import { useNotify } from '@/composables/useNotify'
import {
  addDocuments,
  appendDocumentsIdsToCol,
  prependDocumentsIdsToCol,
  resetColumn
} from '@/store/mutations'
import { getRefreshParamsByMode } from './getters'
import { ApiError, type SearchQueryParams } from 'afpnews-api'
import { isTestUno } from '@/config/testUno'
import router from '@/router'

// Champs strictement nécessaires au parser et à l'UI — évite de rapatrier
// les champs volumineuses inutilisés (body entier, biztags, etc.)
export const DECK_FIELDS = [
  'class',
  'uno',
  'afpshortid',
  'source',
  'headline',
  'created',
  'published',
  'embargoed',
  'lang',
  'afpentity',
  'slug',
  'keyword',
  'country',
  'countryname',
  'city',
  'location',
  'revision',
  'advisory',
  'disclaimer',
  'provider',
  'creator',
  'status',
  'signal',
  'news',
  'genre',
  'urgency',
  'hopHistory',
  'bagItem',
  'caption',
  'captionContext',
  'rating',
  'topic',
  'href'
]

function apiError(error?: unknown) {
  const notify = useNotify()
  const message =
    error instanceof Error ? error.message : 'Une erreur est survenue'
  notify.error(message)
  if (error instanceof ApiError && error.code === 401) {
    void logoutFromApi()
    void router.push({ name: 'login' })
  }
  return Promise.reject(error)
}

export async function searchDocuments(params: SearchQueryParams) {
  try {
    const { documents } = await apicore.search(params, DECK_FIELDS, {
      parse: true,
      lenient: true
    })

    if (!documents) return []

    return addDocuments(documents.filter(d => !isTestUno(d.uno)))
  } catch (error) {
    return apiError(error)
  }
}

export async function refreshColumn(
  columnId: string,
  mode: 'older' | 'newer' | 'reset'
): Promise<string[]> {
  try {
    // Tout se fonde sur l'état Dexie, jamais sur les observables liveQuery :
    // au cold start ils peuvent ne pas être hydratés, et ils peuvent être en
    // retard d'une émission sur les écritures
    const column = await db.columns.get(columnId)
    if (!column) throw new Error('Column not found')

    // Si la colonne est vide, il faut la réinitialiser
    if (column.documentsIds.length === 0) {
      mode = 'reset'
    }

    // Réinitialise la colonne si nécessaire
    if (mode === 'reset') {
      await resetColumn(columnId)
    }

    // Borne du pseudo-curseur : date de publication du document frontière
    let boundaryPublished: Date | undefined
    if (mode !== 'reset') {
      const boundaryId =
        mode === 'newer'
          ? column.documentsIds[0]
          : column.documentsIds[column.documentsIds.length - 1]
      const boundaryDoc = boundaryId
        ? await db.documents.get(boundaryId)
        : undefined
      if (!boundaryDoc) throw new Error('Document not found')
      boundaryPublished = boundaryDoc.published
    }

    // Récupère les params de refresh
    const params = getRefreshParamsByMode(column, mode, boundaryPublished)

    // Fetch un batch de documents, en ignorant ceux déjà dans la colonne :
    // les bornes de date étant inclusives, les documents frontière reviennent
    // à chaque refresh.
    const knownIds =
      mode === 'reset' ? new Set<string>() : new Set(column.documentsIds)
    const batchSize = params.size || 10
    params.size = batchSize + 1
    const {
      count,
      documents: rawDocs,
      skipped = 0
    } = await apicore.search(params, DECK_FIELDS, {
      parse: true,
      lenient: true
    })
    const hasGap = mode === 'newer' && count > rawDocs.length + skipped
    const bufferDocs = rawDocs
      .filter(doc => !knownIds.has(doc.uno))
      .slice(0, batchSize)

    // Parse les documents et les ajoute à la base
    const docs = await addDocuments(bufferDocs)

    // Ajoute les ids de documents à la colonne
    const documentsIds = docs.map(doc => doc.id)
    if (documentsIds.length) {
      if (mode === 'newer') {
        await prependDocumentsIdsToCol(columnId, documentsIds, hasGap)
      } else {
        await appendDocumentsIdsToCol(columnId, documentsIds)
      }
    }

    return documentsIds
  } catch (error) {
    return apiError(error)
  }
}

export async function loadDocumentFromApi(docId: string) {
  console.debug(`Loading document ${docId} from API`)
  try {
    const doc = await apicore.get(docId, { parse: true })
    const docs = await addDocuments([doc])
    if (!docs[0]) throw new Error('Error while loading document')
    return docs[0]
  } catch (error) {
    return apiError(error)
  }
}

// Un même document demandé plusieurs fois en même temps (ex. liens répétés d'une réponse IA) : une seule requête
const pendingLoads = new Map<string, ReturnType<typeof loadDocumentFromApi>>()

export async function loadDocument(docId: string) {
  // Lecture directe dans IndexedDB pour ne jamais retomber sur un appel réseau
  // quand documents.value n'est pas encore peuplé (race liveQuery au cold start).
  const cached = await db.documents.get(docId)
  if (cached) return cached
  let pending = pendingLoads.get(docId)
  if (!pending) {
    pending = loadDocumentFromApi(docId).finally(() =>
      pendingLoads.delete(docId)
    )
    pendingLoads.set(docId, pending)
  }
  return pending
}

export async function loadDocFromShortId(shortId: string) {
  console.debug(`Loading document ${shortId} from API`)
  try {
    const {
      documents: [doc]
    } = await apicore.search(
      {
        afpshortid: shortId,
        size: 1
      },
      DECK_FIELDS,
      { parse: true }
    )
    if (!doc) throw new Error('Document not found')
    const docs = await addDocuments([doc])
    if (!docs[0]) throw new Error('Error while loading document')
    return docs[0]
  } catch (error) {
    return apiError(error)
  }
}
